import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { refundCalculatorCategoryAPI } from '../services/api';
import useListQueryParams from '../hooks/useListQueryParams';
import { PageHeader, FilterBar, EmptyState, StatusChip } from '../components/ui';
import './ModuleAdmin.css';

const DEFAULT_SLAB_ROW = { minDays: '0', maxDays: '', refundPercent: '0' };

const emptyForm = {
  name: '',
  isActive: true,
  order: 0,
  slabs: [
    { minDays: '31', maxDays: '', refundPercent: '90' },
    { minDays: '16', maxDays: '30', refundPercent: '75' },
    { minDays: '8', maxDays: '15', refundPercent: '50' },
    { minDays: '0', maxDays: '7', refundPercent: '0' }
  ]
};

function formatRangeLabel(minDays, maxDays) {
  const min = Number(minDays);
  if (maxDays === '' || maxDays == null) {
    return Number.isFinite(min) ? `${min}+ days` : '—';
  }
  const max = Number(maxDays);
  if (Number.isFinite(min) && Number.isFinite(max)) {
    return min === max ? `${min} days` : `${min}–${max} days`;
  }
  return '—';
}

function slabsFromApi(item) {
  const raw = Array.isArray(item?.slabs) ? item.slabs : [];
  if (!raw.length) return [{ ...DEFAULT_SLAB_ROW }];

  return raw.map((s) => {
    if (s.minDays != null || s.maxDays != null) {
      return {
        minDays: String(s.minDays ?? ''),
        maxDays: s.maxDays == null ? '' : String(s.maxDays),
        refundPercent: String(s.refundPercent ?? '')
      };
    }
    return {
      minDays: String(s.daysBefore ?? ''),
      maxDays: '',
      refundPercent: String(s.refundPercent ?? '')
    };
  });
}

function formatSlabSummary(slabs) {
  if (!Array.isArray(slabs) || !slabs.length) return '—';
  return slabs
    .map((s) => {
      const range =
        s.minDays != null
          ? formatRangeLabel(s.minDays, s.maxDays)
          : `${s.daysBefore}+ days`;
      return `${range} → ${s.refundPercent}%`;
    })
    .join(' · ');
}

const RefundCalculatorCategories = () => {
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();
  const { query, setQuery } = useListQueryParams({ search: '' });
  const search = query.search || '';

  const { data, isLoading } = useQuery(['refund-calculator-categories', search], () =>
    refundCalculatorCategoryAPI.list({ search: search || undefined })
  );
  const items = data?.data?.data || [];

  const createMutation = useMutation((payload) => refundCalculatorCategoryAPI.create(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['refund-calculator-categories']);
      setForm(emptyForm);
      setShowForm(false);
    }
  });

  const updateMutation = useMutation(({ id, payload }) => refundCalculatorCategoryAPI.update(id, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['refund-calculator-categories']);
      setEditing(null);
      setForm(emptyForm);
      setShowForm(false);
    }
  });

  const deleteMutation = useMutation((id) => refundCalculatorCategoryAPI.remove(id), {
    onSuccess: () => queryClient.invalidateQueries(['refund-calculator-categories'])
  });

  const addSlabRow = () => {
    setForm((p) => ({
      ...p,
      slabs: [...(p.slabs || []), { minDays: '', maxDays: '', refundPercent: '' }]
    }));
  };

  const removeSlabRow = (index) => {
    setForm((p) => ({
      ...p,
      slabs: (p.slabs || []).filter((_, i) => i !== index)
    }));
  };

  const updateSlabRow = (index, field, value) => {
    setForm((p) => ({
      ...p,
      slabs: (p.slabs || []).map((row, i) => (i === index ? { ...row, [field]: value } : row))
    }));
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    isActive: Boolean(form.isActive),
    order: Number(form.order) || 0,
    slabs: (form.slabs || []).map((row) => ({
      minDays: Number(row.minDays),
      maxDays: row.maxDays === '' || row.maxDays == null ? null : Number(row.maxDays),
      refundPercent: Number(row.refundPercent)
    }))
  });

  const onSubmit = (e) => {
    e.preventDefault();
    const payload = buildPayload();
    if (editing) {
      updateMutation.mutate({ id: editing, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const onEdit = (item) => {
    setEditing(item.id);
    setShowForm(true);
    setForm({
      name: item.name || '',
      isActive: item.isActive !== false,
      order: item.order ?? 0,
      slabs: slabsFromApi(item)
    });
  };

  return (
    <div className="module-page">
      <PageHeader
        title="Refund Calculator"
        subtitle="Create refund categories with day ranges. Assign a category on each trek to control the cancellation timeline."
        actions={
          !showForm ? (
            <button
              className="module-btn"
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
                setShowForm(true);
              }}
            >
              Add Category
            </button>
          ) : null
        }
      />

      {showForm ? (
        <form className="module-card" onSubmit={onSubmit} style={{ maxWidth: 820 }}>
          <div className="module-form-grid">
            <div style={{ gridColumn: '1 / -1' }}>
              <label>Category name</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
                placeholder="e.g. Local Camps"
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem' }}>Refund slabs</label>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
                Each row is a <strong>day range before departure</strong> (inclusive). Example: Min <strong>16</strong> and Max <strong>30</strong> means 16–30 days before departure → refund %.
                Leave <strong>Max empty</strong> on the top row for no upper limit (e.g. 31+ days).
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr auto',
                    gap: '0.5rem',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: '#6b7280',
                    padding: '0 0.25rem'
                  }}
                >
                  <span>Min days</span>
                  <span>Max days</span>
                  <span>Refund %</span>
                  <span style={{ width: 40 }} />
                </div>
                {(form.slabs || []).map((row, index) => (
                  <div
                    key={`slab-${index}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr auto',
                      gap: '0.5rem',
                      alignItems: 'center'
                    }}
                  >
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.minDays}
                      onChange={(e) => updateSlabRow(index, 'minDays', e.target.value)}
                      placeholder="e.g. 16"
                      required
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={row.maxDays}
                      onChange={(e) => updateSlabRow(index, 'maxDays', e.target.value)}
                      placeholder={index === 0 ? 'blank = 31+' : 'e.g. 30'}
                    />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={row.refundPercent}
                      onChange={(e) => updateSlabRow(index, 'refundPercent', e.target.value)}
                      placeholder="e.g. 50"
                      required
                    />
                    <button
                      type="button"
                      className="module-btn secondary"
                      onClick={() => removeSlabRow(index)}
                      disabled={(form.slabs || []).length <= 1}
                      title="Remove row"
                      style={{ padding: '0.4rem 0.55rem', minWidth: 40 }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="module-btn secondary"
                onClick={addSlabRow}
                style={{ marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
              >
                <AddRoundedIcon fontSize="small" />
                Add slab
              </button>
            </div>

            <div>
              <label>Display order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.isActive)}
                  onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'end', gap: '0.5rem', gridColumn: '1 / -1' }}>
              <button className="module-btn" type="submit" disabled={createMutation.isLoading || updateMutation.isLoading}>
                {editing ? 'Update Category' : 'Create Category'}
              </button>
              <button
                type="button"
                className="module-btn secondary"
                onClick={() => {
                  setEditing(null);
                  setForm(emptyForm);
                  setShowForm(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {!showForm ? (
        <FilterBar>
          <input
            type="text"
            className="module-filter-input"
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setQuery({ search: e.target.value })}
          />
        </FilterBar>
      ) : null}

      <div className="module-card">
        {isLoading ? (
          <p className="module-loading">Loading categories...</p>
        ) : items.length === 0 ? (
          <EmptyState
            title="No refund categories yet"
            description="Create day-range slabs and assign categories on each trek."
            actionLabel={!showForm ? 'Add Category' : undefined}
            onAction={
              !showForm
                ? () => {
                    setEditing(null);
                    setForm(emptyForm);
                    setShowForm(true);
                  }
                : undefined
            }
          />
        ) : (
          <>
          <table className="module-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slabs</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td style={{ maxWidth: 420, fontSize: '0.85rem' }}>{formatSlabSummary(item.slabs)}</td>
                  <td>
                    <StatusChip
                      status={item.isActive === false ? 'inactive' : 'active'}
                      label={item.isActive === false ? 'Inactive' : 'Active'}
                    />
                  </td>
                  <td className="module-actions">
                    <button type="button" className="module-btn secondary" onClick={() => onEdit(item)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="module-btn danger"
                      onClick={() => {
                        if (window.confirm(`Delete "${item.name}"?`)) deleteMutation.mutate(item.id);
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>
    </div>
  );
};

export default RefundCalculatorCategories;
